import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom";
import { FlashpackItem, FlashpackType, isInTrialMode } from "../common/api";
import { BackNavRow, PageContainer } from "../common/CommonStyles";
import { Account, AccountContext } from "../common/context/AccountContext";
import { ApiContext } from "../common/context/ApiContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { TabEnum, TabsContext } from "../common/context/TabsContext";
import {
  getFlashpackAddItemTitle,
  isMathFlashpackType,
  useLiveData,
} from "../common/utils";
import { FlashpackBuilder } from "../components/flashpacks/FlashpackBuilder";
import { MathFlashpackBuilder } from "../components/flashpacks/MathFlashpackBuilder";
import { SubjectSelector } from "../components/flashpacks/SubjectSelector";

export const CreateCustomFlashpackPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { startLoading, stopLoading } = useLoadingIndicator(false);
  const { createCustomFlashpack } = React.useContext(ApiContext);
  const { setCurrentTab } = React.useContext(TabsContext);
  const { accountLiveData } = React.useContext(AccountContext);
  const [isPageAvailable, setIsPageAvailable] = React.useState(false);
  const [selectedFlashpackType, setSelectedFlashpackType] = React.useState<
    FlashpackType | undefined
  >();
  const [deckItems, setDeckItems] = React.useState<FlashpackItem[]>([]);
  const [isValidDeck, setIsValidDeck] = React.useState(false);
  const [selectedSubjectItemsTitle, setSelectedSubjectItemsTitle] =
    React.useState("");

  React.useEffect(() => {
    const flashpackType = location.state?.flashpackType;
    if (flashpackType !== undefined && flashpackType !== FlashpackType.Custom) {
      setSelectedFlashpackType(flashpackType);
    }
  }, [location]);

  React.useEffect(() => {
    setCurrentTab(TabEnum.Practice);
  }, [setCurrentTab]);

  React.useEffect(() => {
    if (selectedFlashpackType) {
      const title = getFlashpackAddItemTitle(t, selectedFlashpackType);
      setSelectedSubjectItemsTitle(title);
    } else {
      setSelectedSubjectItemsTitle("");
    }
  }, [t, selectedFlashpackType, setSelectedSubjectItemsTitle]);

  const saveDeck = React.useCallback(() => {
    if (
      selectedFlashpackType !== undefined &&
      accountLiveData.value.hasMaybeProfileState() &&
      accountLiveData.value.profile !== null
    ) {
      startLoading();
      createCustomFlashpack(
        selectedFlashpackType!,
        accountLiveData.value.profile.id,
        deckItems.map((item) => item.item)
      ).then((resp) => {
        stopLoading();
        history.replace(
          `/lessons/${resp.host_lesson_id}/${resp.slug}/flashpacks/${resp.flashpack.id}/`
        );
      });
    }
  }, [
    selectedFlashpackType,
    accountLiveData,
    startLoading,
    createCustomFlashpack,
    history,
    deckItems,
    stopLoading,
  ]);

  useLiveData(
    React.useCallback(
      (account: Account) =>
        setIsPageAvailable(
          account.hasUserAndMaybeProfile() &&
            account.hasNonGuestProfile() &&
            !isInTrialMode(account.user)
        ),
      [setIsPageAvailable]
    ),
    accountLiveData
  );

  return isPageAvailable ? (
    <PageContainer className="mt-2">
      <BackNavRow
        label={t("generic_nav_back")}
        rightChildren={
          selectedFlashpackType === undefined ? (
            // No "Done" button while selecting subject
            <></>
          ) : isValidDeck ? (
            // Enabled "Done" button if deck can be saved
            <Link
              to=""
              onClick={(event) => {
                saveDeck();
                event.preventDefault();
              }}
              style={{ textDecoration: "none" }}
            >
              {t("generic_done")}
            </Link>
          ) : (
            // Disabled "Done" button if deck cannot be saved
            <span>{t("generic_done")}</span>
          )
        }
        callback={() => {
          if (selectedFlashpackType === undefined) {
            history.goBack();
          } else {
            setDeckItems([]);
            setSelectedFlashpackType(undefined);
          }
        }}
      >
        {selectedFlashpackType === undefined ? (
          <h2>{t("flashpacks_choose_subject")}</h2>
        ) : (
          <h2>
            {t("flashpacks_add_items", { items: selectedSubjectItemsTitle })}
          </h2>
        )}
      </BackNavRow>
      {selectedFlashpackType === undefined ? (
        <SubjectSelector
          onSelectedSubject={(subject) => {
            setSelectedFlashpackType(subject.flashpack_type);
          }}
        />
      ) : isMathFlashpackType(selectedFlashpackType) ? (
        <MathFlashpackBuilder
          flashpackType={selectedFlashpackType}
          deckItems={deckItems}
          setDeckItems={setDeckItems}
          setInvalidDeckErrorMessage={(message: string | null) =>
            setIsValidDeck(message === null)
          }
        />
      ) : (
        <FlashpackBuilder
          flashpackType={selectedFlashpackType}
          deckItems={deckItems}
          setDeckItems={setDeckItems}
          setInvalidDeckErrorMessage={(message: string | null) =>
            setIsValidDeck(message === null)
          }
        />
      )}
    </PageContainer>
  ) : (
    <></>
  );
};
